// src/controllers/customAppController.js
import apiService from '../services/apiService';
import CustomApp  from '../models/CustomApp';

const unwrap = (response) => {
  if (!response) return {};
  if ('success' in response) return response;
  if (response.data && 'success' in response.data) return response.data;
  return response;
};

const SCALAR_FIELDS = [
  'appName', 'packageName', 'description', 'template',
  'primaryColor', 'secondaryColor', 'accentColor',
  'version', 'minSdkVersion', 'targetSdkVersion',
  'status', 'shaKey', // ← shaKey added
];

const buildAppFormData = (appJSON, logoFile, splashFile, iconFile) => {
  const fd = new FormData();

  SCALAR_FIELDS.forEach(key => {
    const val = appJSON[key];
    if (val !== null && val !== undefined && val !== '') {
      fd.append(key, String(val));
    }
  });

  if (Array.isArray(appJSON.features)) {
    fd.append('features', JSON.stringify(appJSON.features));
  }

  if (logoFile)   fd.append('logo',   logoFile);
  if (splashFile) fd.append('splash', splashFile);
  if (iconFile)   fd.append('icon',   iconFile);

  return fd;
};

export const customAppController = {

  // ── GET ALL ────────────────────────────────────────────────────────────────
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint    = queryParams ? `custom-apps?${queryParams}` : 'custom-apps';
      const raw         = unwrap(await apiService.get(endpoint));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list.map(item => CustomApp.fromJSON(item)), total: raw.total || list.length };
    } catch (error) {
      return { success: false, data: [], total: 0, message: error?.response?.data?.message || error.message || 'Failed to fetch apps' };
    }
  },

  // ── GET BY ID ──────────────────────────────────────────────────────────────
  getById: async (id) => {
    try {
      const raw = unwrap(await apiService.get(`custom-apps/${id}`));
      return { success: true, data: CustomApp.fromJSON(raw.data || raw) };
    } catch (error) {
      return { success: false, data: null, message: error?.response?.data?.message || error.message || 'Failed to fetch app' };
    }
  },

  // ── CREATE APP ─────────────────────────────────────────────────────────────
  // ✅ postFormData use karo — NOT post()
  create: async (data, logoFile = null, splashFile = null, iconFile = null) => {
    try {
      const app        = new CustomApp(data);
      const validation = app.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }

      const fd = buildAppFormData(app.toJSON(), logoFile, splashFile, iconFile);

      console.log('Creating app FormData fields:');
      for (let [k, v] of fd.entries()) {
        console.log(`  ${k}:`, v instanceof File ? `File(${v.name})` : v);
      }

      // ✅ postFormData — browser sets Content-Type + boundary automatically
      const raw = unwrap(await apiService.postFormData('custom-apps', fd));

      if (raw.success === false) {
        return { success: false, errors: raw.errors || [], message: raw.message };
      }

      return { success: true, data: CustomApp.fromJSON(raw.data || raw), message: raw.message || 'App created successfully' };
    } catch (error) {
      console.error('Create app error:', error);
      const errData = error?.data;
      return {
        success: false,
        message: errData?.message || error.message || 'Failed to create app',
        errors:  errData?.errors  || [],
      };
    }
  },

  // ── UPDATE APP ─────────────────────────────────────────────────────────────
  // ✅ putFormData use karo — NOT put()
  update: async (id, data, logoFile = null, splashFile = null, iconFile = null) => {
    try {
      const app        = new CustomApp({ ...data, id });
      const validation = app.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }

      const fd = buildAppFormData(app.toJSON(), logoFile, splashFile, iconFile);

      // ✅ putFormData — browser sets Content-Type + boundary automatically
      const raw = unwrap(await apiService.putFormData(`custom-apps/${id}`, fd));

      if (raw.success === false) {
        return { success: false, errors: raw.errors || [], message: raw.message };
      }

      return { success: true, data: CustomApp.fromJSON(raw.data || raw), message: raw.message || 'App updated successfully' };
    } catch (error) {
      console.error('update error:', error);
      const errData = error?.data;
      return {
        success: false,
        message: errData?.message || error.message || 'Failed to update app',
        errors:  errData?.errors  || [],
      };
    }
  },

  // ── DELETE ─────────────────────────────────────────────────────────────────
  delete: async (id) => {
    try {
      const raw = unwrap(await apiService.delete(`custom-apps/${id}`));
      return { success: true, message: raw.message || 'App deleted successfully' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to delete app' };
    }
  },

  // ── BULK DELETE ────────────────────────────────────────────────────────────
  bulkDelete: async (ids) => {
    try {
      const raw = unwrap(await apiService.post('custom-apps/bulk-delete', { ids }));
      return { success: true, message: raw.message || `${ids.length} app(s) deleted` };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to delete apps' };
    }
  },

  // ── BUILD ──────────────────────────────────────────────────────────────────
  buildApp: async (id, buildOptions = {}) => {
    try {
      const raw = unwrap(await apiService.post(`custom-apps/${id}/build`, buildOptions));
      return { success: true, data: raw.data || raw, message: raw.message || 'Build started' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to start build' };
    }
  },

  getBuildStatus: async (id, buildId) => {
    try {
      const raw = unwrap(await apiService.get(`custom-apps/${id}/builds/${buildId}`));
      return { success: true, data: raw.data || raw };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  getBuildHistory: async (id) => {
    try {
      const raw = unwrap(await apiService.get(`custom-apps/${id}/builds`));
      return { success: true, data: raw.data || [] };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  downloadBuild: async (id, buildId) => {
    try {
      const res = await apiService.get(`custom-apps/${id}/builds/${buildId}/download`);
      return { success: true, data: res.data || res };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // ── ✅ UPLOAD APK / AAB ────────────────────────────────────────────────────
  // ✅ postFormData use karo — NOT post()
  uploadAPK: async (id, apkFile, apkData = {}) => {
    try {
      if (!apkFile) return { success: false, message: "APK file is required" };

      const {
        versionName, versionCode,shaKey,
        track = "production", rolloutPercentage = 100,
        releaseNotes = "", minSdkVersion = "21", targetSdkVersion = "33",
      } = apkData;

      if (!versionName?.toString().trim()) return { success: false, errors: ["Version name is required."] };
      if (!versionCode?.toString().trim())  return { success: false, errors: ["Version code is required."] };
      if (!shaKey?.toString().trim())        return { success: false, errors: ["SHA key is required."] };  

      const formData = new FormData();
      formData.append("apkFile",            apkFile);                        // ✅ File object directly
      formData.append("versionName",        versionName.toString().trim());
      formData.append("versionCode",        versionCode.toString());
      formData.append("shaKey", shaKey.toString().trim());
      formData.append("track",              track);
      formData.append("rolloutPercentage",  rolloutPercentage.toString());
      formData.append("releaseNotes",       releaseNotes);
      formData.append("minSdkVersion",      minSdkVersion);
      formData.append("targetSdkVersion",   targetSdkVersion);

      console.log("Uploading APK FormData:");
      for (let pair of formData.entries()) {
        console.log(" ", pair[0], pair[1] instanceof File ? `File(${pair[1].name}, ${pair[1].size} bytes)` : pair[1]);
      }

      // ✅ postFormData — NOT post()
      const raw = unwrap(await apiService.postFormData(`custom-apps/${id}/apk`, formData));

      if (raw.success === false) {
        return { success: false, message: raw.message, errors: raw.errors || [] };
      }

      return { success: true, data: raw.data || raw, message: raw.message || "APK uploaded successfully" };
    } catch (error) {
      console.error("uploadAPK error:", error);
      const errData = error?.data;
      return {
        success: false,
        message: errData?.message || error.message || "Failed to upload APK",
        errors:  errData?.errors  || [],
      };
    }
  },
  publishAPK: async (id, apkId) => {
  try {
    const raw = unwrap(await apiService.patch(`custom-apps/${id}/apk/${apkId}/publish`, {}));
    if (raw.success === false) return { success: false, message: raw.message };
    return { success: true, data: raw.data, message: raw.message || 'APK published successfully' };
  } catch (error) {
    return { success: false, message: error?.data?.message || error.message || 'Failed to publish APK' };
  }
},
// ── UPDATE APK TRACK ───────────────────────────────────────────────────────
updateAPKTrack: async (id, apkId, track) => {
  try {
    // 1. pehle track patch karo
    const raw = unwrap(await apiService.patch(
      `custom-apps/${id}/apk/${apkId}/track`,
      { track }
    ));
    if (raw.success === false) {
      return { success: false, message: raw.message };
    }
    return { success: true, data: raw.data || raw, message: raw.message || 'Track updated' };
  } catch (error) {
    return { success: false, message: error?.data?.message || error.message || 'Failed to update track' };
  }
},
  // ── GET APK HISTORY ────────────────────────────────────────────────────────
  getAPKHistory: async (id) => {
    try {
      const raw  = unwrap(await apiService.get(`custom-apps/${id}/apk-history`));
      const list = Array.isArray(raw.data) ? raw.data : [];
      return { success: true, data: list, total: raw.total || list.length };
    } catch (error) {
      return { success: false, data: [], message: error?.data?.message || error.message || 'Failed to fetch APK history' };
    }
  },

  // ── DOWNLOAD LATEST APK ────────────────────────────────────────────────────
  downloadLatestAPK: async (id) => {
    try {
      const raw     = unwrap(await apiService.get(`custom-apps/${id}/apk/download`));
      const payload = raw.data || raw;
      return { success: true, downloadUrl: payload.downloadUrl || payload.apkUrl || null, fileName: payload.fileName || 'app.apk', versionName: payload.versionName || '', fileSize: payload.fileSize || '' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'No APK available' };
    }
  },

  // ── DOWNLOAD SPECIFIC APK ──────────────────────────────────────────────────
  downloadAPK: async (id, apkId) => {
    try {
      const raw     = unwrap(await apiService.get(`custom-apps/${id}/apk/${apkId}/download`));
      const payload = raw.data || raw;
      return { success: true, downloadUrl: payload.downloadUrl || payload.apkUrl || null, fileName: payload.fileName || 'app.apk', versionName: payload.versionName || '', fileSize: payload.fileSize || '' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to get download link' };
    }
  },

  // ── SET ACTIVE APK ─────────────────────────────────────────────────────────
  setActiveAPK: async (id, apkId) => {
    try {
      const raw = unwrap(await apiService.patch(`custom-apps/${id}/apk/${apkId}/activate`, {}));
      return { success: true, data: raw.data || raw, message: raw.message || 'APK activated' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to activate APK' };
    }
  },

  // ── STATUS / PUBLISH / CLONE ──────────────────────────────────────────────
  updateStatus: async (id, status) => {
    try {
      const raw = unwrap(await apiService.patch(`custom-apps/${id}/status`, { status }));
      return { success: true, data: CustomApp.fromJSON(raw.data || raw), message: raw.message || 'Status updated' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to update status' };
    }
  },

  publishApp: async (id) => {
    try {
      const raw = unwrap(await apiService.post(`custom-apps/${id}/publish`, {}));
      return { success: true, data: CustomApp.fromJSON(raw.data || raw), message: raw.message || 'App published' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to publish' };
    }
  },

  cloneApp: async (id, newAppName) => {
    try {
      const raw = unwrap(await apiService.post(`custom-apps/${id}/clone`, { newAppName }));
      return { success: true, data: CustomApp.fromJSON(raw.data || raw), message: raw.message || 'App cloned' };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Failed to clone' };
    }
  },

  // ── EXPORT ─────────────────────────────────────────────────────────────────
  export: async (filters = {}, format = 'csv') => {
    try {
      const qp  = new URLSearchParams({ ...filters, format }).toString();
      const res = await apiService.get(`custom-apps/export?${qp}`);
      return { success: true, data: res.data || res };
    } catch (error) {
      return { success: false, message: error?.data?.message || error.message || 'Export failed' };
    }
  },

  // ── STATISTICS ─────────────────────────────────────────────────────────────
  getStatistics: async (filters = {}) => {
    try {
      const qp  = new URLSearchParams(filters).toString();
      const raw = unwrap(await apiService.get(qp ? `custom-apps/statistics?${qp}` : 'custom-apps/statistics'));
      return { success: true, data: raw.data || raw };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // ── TEMPLATES ──────────────────────────────────────────────────────────────
  getTemplates: async () => {
    try {
      const raw = unwrap(await apiService.get('custom-apps/templates'));
      return { success: true, data: raw.data || [] };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // ── VALIDATE PACKAGE NAME ──────────────────────────────────────────────────
  validatePackageName: async (packageName) => {
    try {
      const raw     = unwrap(await apiService.post('custom-apps/validate-package', { packageName }));
      const payload = raw.data || raw;
      return { success: true, available: payload.available, message: payload.message };
    } catch (error) {
      return { success: false, available: false, message: error.message };
    }
  },

  // ── ANALYTICS ──────────────────────────────────────────────────────────────
  getAnalytics: async (id, dateRange = {}) => {
    try {
      const qp  = new URLSearchParams(dateRange).toString();
      const raw = unwrap(await apiService.get(qp ? `custom-apps/${id}/analytics?${qp}` : `custom-apps/${id}/analytics`));
      return { success: true, data: raw.data || raw };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },
};

export default customAppController;